// §11.6 — the Settings pages M13.7 fills in: Keyboard, Default apps, Apps, Account, About.
//
// They are here rather than in `Settings.tsx` because that file is the *shell* of the app — the
// nav, the page switch, and Personalization — and five more pages inside it would bury it.
import { useCallback, useEffect, useState } from 'react';
import type {
  AboutView,
  AccountView,
  FileTypeView,
  InstalledAppView,
  KeyBindingView,
  SettingsShellAccess,
} from '@hangar/shared';
import { COPY } from '@/copy';

/**
 * §11.6 Keyboard — "view the active keymap; per-binding override".
 *
 * Capture, not a text box: a chord is a key event, and asking someone to *type* `Meta+Shift+V`
 * turns a shortcut into a spelling test. §6.4's parser does the reading, through the capability,
 * because a second implementation in an app is exactly the drift the single parser prevents.
 */
export function KeyboardPage({ access }: { access: SettingsShellAccess }) {
  const [rows, setRows] = useState<readonly KeyBindingView[]>(() => access.keyBindings());
  const [recording, setRecording] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(() => setRows(access.keyBindings()), [access]);
  useEffect(() => access.subscribe(refresh), [access, refresh]);

  useEffect(() => {
    if (recording === null) return;
    const onKey = (event: KeyboardEvent): void => {
      // Everything, including chords the shell itself has claimed — otherwise the page could not
      // rebind the very shortcuts that most need rebinding.
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') {
        setRecording(null);
        return;
      }
      const chord = access.chordOf({
        key: event.key,
        // The physical key, which is what §6.4 canonicalises from — `Shift+9` must record as
        // `Shift+9`, not as the `(` this layout happens to produce.
        code: event.code,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
      });
      // A bare modifier is not a chord yet — they are still on their way to one.
      if (chord === null) return;

      const result = access.setBinding(recording, chord);
      if (result === 'ok') {
        setRecording(null);
        setProblem(null);
        refresh();
        return;
      }
      setProblem(
        result === 'conflict'
          ? COPY.settings.bindingConflict
          : result === 'browser'
            ? COPY.settings.bindingBrowser
            : COPY.settings.bindingInvalid,
      );
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [access, recording, refresh]);

  return (
    <section className="settings-group" aria-labelledby="settings-keyboard-title">
      <h3 className="settings-group-title" id="settings-keyboard-title">
        {COPY.settings.pages.keyboard}
      </h3>
      <p className="settings-hint">{COPY.settings.keyboardHint}</p>
      {problem ? (
        <p className="settings-problem" role="alert" data-testid="settings-binding-problem">
          {problem}
        </p>
      ) : null}
      <ul className="settings-rows" data-testid="settings-bindings">
        {rows.map((row) => (
          <li key={row.id} className="settings-row" data-testid={`settings-binding-${row.id}`}>
            <span className="settings-row-label">{row.label}</span>
            {row.reserved ? (
              <span className="settings-tag" title={COPY.settings.reservedTag}>
                {COPY.settings.reservedTag}
              </span>
            ) : null}
            <span className="settings-spacer" />
            <button
              type="button"
              className="settings-chord"
              data-testid={`settings-binding-chord-${row.id}`}
              data-recording={String(recording === row.id)}
              data-overridden={String(row.overridden)}
              onClick={() => {
                setProblem(null);
                setRecording((current) => (current === row.id ? null : row.id));
              }}
            >
              {recording === row.id ? COPY.settings.recording : row.display}
            </button>
            <button
              type="button"
              data-testid={`settings-binding-reset-${row.id}`}
              disabled={!row.overridden}
              onClick={() => {
                access.setBinding(row.id, null);
                refresh();
              }}
            >
              {COPY.settings.resetBinding}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** §11.6 Default apps — §9.2's registry, with the user's choice on top of it. */
export function DefaultAppsPage({ access }: { access: SettingsShellAccess }) {
  const [rows, setRows] = useState<readonly FileTypeView[]>(() => access.fileTypes());
  const refresh = useCallback(() => setRows(access.fileTypes()), [access]);
  useEffect(() => access.subscribe(refresh), [access, refresh]);

  return (
    <section className="settings-group" aria-labelledby="settings-defaults-title">
      <h3 className="settings-group-title" id="settings-defaults-title">
        {COPY.settings.pages.defaultApps}
      </h3>
      <p className="settings-hint">{COPY.settings.defaultAppsHint}</p>
      <ul className="settings-rows" data-testid="settings-filetypes">
        {rows.map((row) => (
          <li key={row.ext} className="settings-row" data-testid={`settings-filetype-${row.ext}`}>
            <span className="settings-row-label settings-ext">{row.ext}</span>
            <span className="settings-spacer" />
            {row.candidates.length === 0 ? (
              <span className="settings-muted">{COPY.settings.noCandidates}</span>
            ) : (
              <select
                data-testid={`settings-filetype-select-${row.ext}`}
                value={row.current}
                onChange={(event) => {
                  access.setDefaultApp(row.ext, event.target.value);
                  refresh();
                }}
              >
                {row.candidates.map((candidate) => (
                  <option key={candidate.appId} value={candidate.appId}>
                    {candidate.name}
                  </option>
                ))}
              </select>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** §11.6 Apps — installed, permissions, health (§9.6). */
export function AppsPage({ access }: { access: SettingsShellAccess }) {
  const [apps, setApps] = useState<readonly InstalledAppView[]>(() => access.installedApps());
  const refresh = useCallback(() => setApps(access.installedApps()), [access]);

  if (apps.length === 0) {
    return (
      <section className="settings-group">
        <p className="settings-muted" data-testid="settings-apps-empty">
          {COPY.settings.noApps}
        </p>
      </section>
    );
  }

  return (
    <section className="settings-group" aria-labelledby="settings-apps-title">
      <h3 className="settings-group-title" id="settings-apps-title">
        {COPY.settings.pages.apps}
      </h3>
      <ul className="settings-rows" data-testid="settings-apps">
        {apps.map((app) => (
          <li key={app.id} className="settings-app" data-testid={`settings-app-${app.id}`}>
            <div className="settings-row">
              <span className="settings-row-label">
                {app.name} <span className="settings-muted">{app.version}</span>
              </span>
              <span className="settings-spacer" />
              <span
                className="settings-health"
                data-testid={`settings-app-health-${app.id}`}
                data-health={app.health}
              >
                {COPY.settings.health(app.health)}
              </span>
              <button
                type="button"
                data-testid={`settings-app-uninstall-${app.id}`}
                onClick={() => void access.uninstallApp(app.id).then(refresh)}
              >
                {COPY.settings.uninstall}
              </button>
            </div>
            <p className="settings-muted settings-origin">{app.origin}</p>
            <p className="settings-permissions" data-testid={`settings-app-permissions-${app.id}`}>
              {app.granted.length > 0 ? `${COPY.settings.granted}: ${app.granted.join(', ')}` : null}
              {app.requested.length > 0 ? (
                <span className="settings-ungranted">
                  {` ${COPY.settings.notGranted}: ${app.requested.join(', ')}`}
                </span>
              ) : null}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** §11.6 Account — sign in/out, and for an admin §8.3's anonymous-access toggle. */
export function AccountPage({ access }: { access: SettingsShellAccess }) {
  const [view, setView] = useState<AccountView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(() => {
    void access.account().then(setView);
  }, [access]);
  useEffect(refresh, [refresh]);

  if (!view) return <p data-testid="settings-account-loading">…</p>;

  return (
    <section className="settings-group" aria-labelledby="settings-account-title">
      <h3 className="settings-group-title" id="settings-account-title">
        {COPY.settings.pages.account}
      </h3>
      <p className="settings-readout" data-testid="settings-account-who">
        {view.signedIn && view.displayName !== null
          ? COPY.settings.signedInAs(view.displayName)
          : COPY.settings.guestAccount}
      </p>

      {view.signedIn ? (
        <button type="button" data-testid="settings-sign-out" onClick={() => void access.signOut()}>
          {COPY.settings.signOut}
        </button>
      ) : (
        <button type="button" data-testid="settings-sign-in" onClick={() => access.signIn()}>
          {COPY.settings.signIn}
        </button>
      )}

      {/* §8.3 — an admin's toggle. Shown disabled rather than hidden for everyone else: a control
          nobody can see is a feature nobody knows exists, and the *server* is what enforces it. */}
      <label className="settings-toggle">
        <input
          type="checkbox"
          data-testid="settings-anonymous-access"
          checked={view.anonymousAccess === true}
          disabled={!view.isAdmin || !view.hasServer}
          onChange={(event) => {
            const wanted = event.target.checked;
            void access.setAnonymousAccess(wanted).then((settled) => {
              if (settled === null) setProblem(COPY.settings.anonymousFailed);
              else setProblem(null);
              refresh();
            });
          }}
        />
        {COPY.settings.anonymousToggle}
      </label>
      <p className="settings-muted" data-testid="settings-anonymous-note">
        {!view.hasServer
          ? COPY.settings.anonymousNoServer
          : view.isAdmin
            ? ''
            : COPY.settings.anonymousAdminOnly}
      </p>
      {problem ? (
        <p className="settings-problem" role="alert" data-testid="settings-account-problem">
          {problem}
        </p>
      ) : null}
    </section>
  );
}

/** §11.6 About — the version, and D-042's browser-capability statement in plain words. */
export function AboutPage({ access }: { access: SettingsShellAccess }) {
  const [view] = useState<AboutView>(() => access.about());

  return (
    <section className="settings-group" aria-labelledby="settings-about-title">
      <h3 className="settings-group-title" id="settings-about-title">
        {COPY.settings.pages.about}
      </h3>
      <p className="settings-readout" data-testid="settings-version">
        {COPY.settings.version(view.version)}
      </p>

      <h4 className="settings-subtitle">{COPY.settings.capabilityTitle}</h4>
      <ul className="settings-rows" data-testid="settings-capabilities">
        {view.capabilities.map((capability) => (
          <li
            key={capability.id}
            className="settings-row"
            data-testid={`settings-capability-${capability.id}`}
            data-present={String(capability.present)}
          >
            <span className="settings-row-label">{capability.label}</span>
            <span className="settings-spacer" />
            <span className="settings-capability-answer">
              {capability.present ? COPY.settings.capabilityYes : COPY.settings.capabilityNo}
            </span>
            {/* D-042 — a "no" is only useful next to what to do instead. */}
            {capability.present ? null : (
              <span className="settings-muted">{capability.consequence}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
